<template>
    <div :class="['item', { active: isActive, locked: isCascadeLocked }]">
        <div :class="['scenario-row', { 'scenario-row--clickable': isRowClickable }]" @click="onRowClick">
            <div class="ico">
                <v-icon size="19">{{ mdiIcon(scenario.meta.icon) }}</v-icon>
            </div>
            <div class="body">
                <div class="b-top">
                    <span class="name">{{ scenario.name }}</span>
                    <span :class="['badge', roleLabels[scenario.role].className]">
                        {{ roleLabels[scenario.role].label }}
                    </span>
                    <span v-if="scenario.automail" class="badge auto">
                        <v-icon class="badge-auto__icon" size="10">mdi-send</v-icon>
                        Авторассылка
                    </span>
                </div>
                <div class="desc">{{ scenario.meta.description }}</div>
            </div>
            <div :class="['right', { 'right--with-off': hasDisableButton }]">
                <span v-if="isCascadeLocked" class="lock-chip" aria-hidden="true">
                    <v-icon size="15">mdi-lock-outline</v-icon>
                </span>
                <button
                    v-if="showConfigureButton(scenario)"
                    type="button"
                    :class="['cfg', getConfigureButtonClasses(scenario)]"
                    @click.stop="$emit('configure')"
                >
                    <v-icon class="cfg__icon" size="12">{{ getConfigureButtonIcon(scenario) }}</v-icon>
                    {{ getConfigureButtonLabel(scenario) }}
                </button>
                <span v-else-if="isActive" class="cfg active cfg--readonly">
                    <v-icon class="cfg__icon" size="12">mdi-check</v-icon>
                    Активен
                </span>
                <v-tooltip v-else bottom :disabled="canSaveSettings">
                    <template v-slot:activator="{ on, attrs }">
                        <span class="launch-btn-wrap" v-bind="attrs" v-on="on">
                            <button
                                class="launch-btn"
                                :disabled="launching || !canSaveSettings"
                                @click="$emit('launch')"
                            >
                                {{ launching ? 'Запуск...' : 'Запустить' }}
                            </button>
                        </span>
                    </template>
                    <span>{{ saveAccessDeniedHint }}</span>
                </v-tooltip>
                <button v-if="hasDisableButton" class="off-btn" :disabled="launching" @click.stop="$emit('disable')">
                    {{ launching ? '...' : 'Отключить' }}
                </button>
                <span v-if="!isActive" class="status off">
                    <span class="dot"></span>Не запущен
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import {
    mdiIcon,
    showConfigureButton,
    showDisableButton,
    isScenarioActive,
    isScenarioCascadeLocked,
    getConfigureButtonLabel,
    getConfigureButtonIcon,
    getConfigureButtonClasses,
} from '../utils/scenarioUi'
import { STARTER_PACK_SAVE_ACCESS_DENIED_HINT } from '../constants'

export default {
    name: 'StarterPackScenarioItem',
    props: {
        scenario: {
            type: Object,
            required: true,
        },
        roleLabels: {
            type: Object,
            required: true,
        },
        launching: {
            type: Boolean,
            default: false,
        },
        canSaveSettings: {
            type: Boolean,
            default: false,
        },
        scenarios: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        isActive() {
            return isScenarioActive(this.scenario)
        },
        isCascadeLocked() {
            return isScenarioCascadeLocked(this.scenario, this.scenarios)
        },
        isRowClickable() {
            return this.isCascadeLocked || showConfigureButton(this.scenario)
        },
        hasDisableButton() {
            return showDisableButton(this.scenario)
        },
        saveAccessDeniedHint() {
            return STARTER_PACK_SAVE_ACCESS_DENIED_HINT
        },
    },
    methods: {
        mdiIcon,
        showConfigureButton,
        getConfigureButtonLabel,
        getConfigureButtonIcon,
        getConfigureButtonClasses,
        onRowClick() {
            if (this.isCascadeLocked) {
                this.$emit('locked-click')
                return
            }
            if (showConfigureButton(this.scenario)) {
                this.$emit('configure')
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import '@/vars.sass';

.item {
    width: 100%;
    min-width: 0;
    overflow: hidden;
    background: #fff;
    border: 1px solid $border-new;
    border-radius: 13px;
    transition: border-color 0.18s, background 0.18s;
}

.item.active {
    border-color: #cde7d8;
}

.item.locked {
    opacity: 0.64;
}

.item.locked .cfg,
.item.locked .launch-btn {
    pointer-events: none;
}

.scenario-row--clickable {
    cursor: pointer;
}

.lock-chip {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    color: #9aa2b2;
    background: #eef1f6;
    border-radius: 8px;
}

.scenario-row {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
    padding: 15px 18px;
}

.ico {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    background: #f4f6fa;
    border: 1px solid $border-light-new;
    border-radius: 10px;
}

.ico ::v-deep .v-icon {
    color: #7a8294;
}

.body {
    flex: 1;
    min-width: 0;
}

.b-top {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 9px;
    margin-bottom: 3px;
}

.name {
    color: $dark-new;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: -0.01em;
    overflow-wrap: anywhere;
}

.desc {
    color: $muted-new;
    font-size: 12px;
    line-height: 1.4;
    overflow-wrap: anywhere;
}

.badge {
    display: inline-flex;
    align-items: center;
    padding: 3px 9px;
    border-radius: 999px;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 9.5px;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    white-space: nowrap;
}

.badge.admin {
    color: $role-blue-new;
    background: $role-blue-light-new;
}

.badge.master {
    color: $role-green-new;
    background: $role-green-light-new;
}

.badge.manager {
    color: $role-violet-new;
    background: $role-violet-light-new;
}

.badge.auto {
    gap: 4px;
    padding-left: 7px;
    color: #fff;
    background: #e8843b;
}

.badge-auto__icon {
    color: #fff !important;
}

.right {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 14px;
}

.status {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    min-width: 96px;
    gap: 7px;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
}

.status .dot {
    flex-shrink: 0;
    width: 7px;
    height: 7px;
    border-radius: 50%;
}

.status.off {
    color: $muted-light-new;
}

.status.off .dot {
    background: #cfd4de;
}

.status.on {
    color: $success-new;
}

.status.on .dot {
    background: $success-new;
    box-shadow: 0 0 0 3px -light-new;
}

.cfg {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 7px 12px;
    color: $main-new;
    background: #fff;
    border: 1.4px solid #d2d6f2;
    border-radius: 8px;
    cursor: pointer;
    font-family: Geologica, Inter, system-ui, sans-serif;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    white-space: nowrap;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.15s ease;

    &:hover:not(:disabled):not(.cfg--readonly) {
        background: $bcg-card-new;
        border-color: #bdc3ee;
    }

    &:active:not(:disabled):not(.cfg--readonly) {
        transform: scale(0.98);
    }
}

.cfg.active {
    color: $success-new;
    background: $success-light-new;
    border-color: #bfe3cc;

    &:hover:not(.cfg--readonly) {
        background: #dff3e6;
        border-color: #9dd4b0;
    }

    &:active:not(.cfg--readonly) {
        background: #d4edd9;
        border-color: #8bc9a0;
        transform: scale(0.98);
    }
}

.cfg.cfg--readonly {
    cursor: default;
}

.cfg__icon {
    color: inherit !important;
}

.launch-btn-wrap {
    display: inline-flex;
}

.launch-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 8px 14px;
    color: #fff;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    white-space: nowrap;
    background: $main-new;
    border: none;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(82, 90, 213, 0.26);
    cursor: pointer;
    transition: background 0.15s ease, transform 0.15s ease;

    &:hover:not(:disabled) {
        background: #454cc0;
        transform: translateY(-1px);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

.off-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 7px 12px;
    color: #c0392b;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    white-space: nowrap;
    background: #fff;
    border: 1.4px solid #f0c9c5;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover:not(:disabled) {
        background: #fdedec;
        border-color: #e0473a;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

@media (max-width: 760px) {
    .item {
        position: relative;
        border-radius: 16px;
        overflow: visible;
    }

    .scenario-row {
        display: block;
        padding: 16px;
    }

    .ico {
        display: none;
    }

    .body {
        width: 100%;
        min-width: 0;
    }

    .b-top {
        gap: 6px;
        margin-bottom: 0;
    }

    .name {
        flex: 0 0 100%;
        width: 100%;
        font-size: 17px;
        line-height: 1.28;
        letter-spacing: -0.015em;
        padding-right: 0;
    }

    .item.locked .name {
        padding-right: 38px;
    }

    .desc {
        margin-top: 10px;
        font-size: 13.5px;
        line-height: 1.5;
    }

    .right {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        align-items: center;
        gap: 8px;
        width: 100%;
        margin-top: 14px;
        padding-left: 0;
    }

    .status {
        order: -1;
        grid-column: 1 / -1;
        width: max-content;
        max-width: 100%;
        min-width: 0;
        justify-content: flex-start;
        padding: 6px 10px;
        border-radius: 999px;
        background: #f2f4f7;
        font-size: 11px;
        line-height: 1;
    }

    .status.off {
        color: #8a93a6;
    }

    .status.on {
        background: $success-light-new;
    }

    .cfg,
    .launch-btn,
    .launch-btn-wrap {
        grid-column: 1 / -1;
        width: 100%;
    }

    .cfg,
    .launch-btn {
        min-height: 44px;
        justify-content: center;
        border-radius: 10px;
        padding: 10px 14px;
        font-size: 11.5px;
    }

    .launch-btn-wrap {
        display: flex;
    }

    .launch-btn-wrap .launch-btn {
        width: 100%;
    }

    // «Активен» + «Отключить» в один ряд
    .right--with-off {
        grid-template-columns: minmax(0, 1fr) auto;
    }

    .right--with-off .cfg,
    .right--with-off .launch-btn,
    .right--with-off .launch-btn-wrap {
        grid-column: 1;
        width: auto;
        min-width: 0;
    }

    .right--with-off .off-btn {
        grid-column: 2;
        width: auto;
        flex-shrink: 0;
        justify-content: center;
        white-space: nowrap;
        min-height: 44px;
        border-radius: 10px;
        padding: 10px 13px;
        font-size: 10.5px;
    }

    .off-btn {
        white-space: nowrap;
    }

    .lock-chip {
        position: absolute;
        top: 14px;
        right: 14px;
        width: 32px;
        height: 32px;
        border-radius: 9px;
        z-index: 2;
    }

    .item.locked .cfg {
        opacity: 0.75;
    }
}
</style>
